import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { showSuccess, showError } from "@/utils/toast";
import { ChevronDown, ChevronRight, Image as ImageIcon, Download, Trash2, RefreshCw, Loader2, Tag } from "lucide-react";

interface Library {
  id: number;
  name: string;
  children?: Library[];
  children_count: number;
  total_images_count: number;
  total_slices_count: number;
  unmapped_slices_count: number;
  closed: boolean;
  hidden: boolean;
}

interface Slice {
  id: number;
  image_id: number;
  slice_number: number;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  slice_url: string | null;
  has_product_mapping: boolean;
  created_at: string;
  updated_at: string;
}

interface LibraryWithSlices extends Library {
  slices?: Slice[];
  slicesLoaded?: boolean;
  slicesLoading?: boolean;
}

const SliceList = forwardRef((_, ref) => {
  const navigate = useNavigate();
  const [libraries, setLibraries] = useState<LibraryWithSlices[]>([]);
  const [expandedLibraries, setExpandedLibraries] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);

  useImperativeHandle(ref, () => ({
    refresh: fetchLibraries,
  }));

  useEffect(() => {
    fetchLibraries();
  }, []);

  const fetchLibraries = async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/admin/libraries");
      if (response.ok) {
        const data = await response.json();
        setLibraries(data.libraries || []);
        setExpandedLibraries(new Set());
      } else {
        showError("Nem sikerült a könyvtárak betöltése.");
      }
    } catch (error) {
      console.error("Hiba a könyvtárak betöltésekor:", error);
      showError("Hiba a könyvtárak betöltésekor.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSlicesForLibrary = async (libraryId: number) => {
    try {
      const response = await fetch(`/api/admin/libraries/${libraryId}/slices`);
      if (response.ok) {
        const data = await response.json();
        updateLibrarySlices(libraryId, data.slices || [], data.unmapped_slices_count || 0);
      } else {
        showError("Nem sikerült a slice-ok betöltése.");
      }
    } catch (error) {
      console.error("Hiba a slice-ok betöltésekor:", error);
      showError("Hiba a slice-ok betöltésekor.");
    }
  };

  const updateLibrarySlices = (libraryId: number, slices: Slice[], unmappedCount: number = 0) => {
    setLibraries((prevLibraries) =>
      prevLibraries.map((lib) => {
        if (lib.id === libraryId) {
          return { ...lib, slices, slicesLoaded: true, slicesLoading: false, unmapped_slices_count: unmappedCount };
        }
        if (lib.children) {
          return { ...lib, children: updateLibrarySlicesRecursive(lib.children, libraryId, slices, unmappedCount) };
        }
        return lib;
      })
    );
  };

  const updateLibrarySlicesRecursive = (libs: LibraryWithSlices[], libraryId: number, slices: Slice[], unmappedCount: number = 0): LibraryWithSlices[] => {
    return libs.map((lib) => {
      if (lib.id === libraryId) {
        return { ...lib, slices, slicesLoaded: true, slicesLoading: false, unmapped_slices_count: unmappedCount };
      }
      if (lib.children) {
        return { ...lib, children: updateLibrarySlicesRecursive(lib.children, libraryId, slices, unmappedCount) };
      }
      return lib;
    });
  };

  const toggleLibrary = (libraryId: number) => {
    const newExpanded = new Set(expandedLibraries);
    if (newExpanded.has(libraryId)) {
      newExpanded.delete(libraryId);
    } else {
      newExpanded.add(libraryId);
      setLibraries((prevLibraries) =>
        prevLibraries.map((lib) => {
          if (lib.id === libraryId && !lib.slicesLoaded) {
            fetchSlicesForLibrary(libraryId);
            return { ...lib, slicesLoading: true };
          }
          if (lib.children) {
            return { ...lib, children: markLibraryLoadingRecursive(lib.children, libraryId) };
          }
          return lib;
        })
      );
    }
    setExpandedLibraries(newExpanded);
  };

  const markLibraryLoadingRecursive = (libs: LibraryWithSlices[], libraryId: number): LibraryWithSlices[] => {
    return libs.map((lib) => {
      if (lib.id === libraryId && !lib.slicesLoaded) {
        fetchSlicesForLibrary(libraryId);
        return { ...lib, slicesLoading: true };
      }
      if (lib.children) {
        return { ...lib, children: markLibraryLoadingRecursive(lib.children, libraryId) };
      }
      return lib;
    });
  };

  const handleDeleteSlice = async (sliceId: number) => {
    if (!confirm("Biztosan törölni szeretnéd ezt a slice-ot?")) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/slices/${sliceId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        showSuccess("Slice sikeresen törölve!");
        fetchLibraries();
      } else {
        showError("Hiba a slice törlése során.");
      }
    } catch (error) {
      console.error("Hiba a törlés során:", error);
      showError("Hiba a törlés során.");
    }
  };

  const handleGroupAnnotation = (library: LibraryWithSlices) => {
    navigate(`/admin/annotator/${library.id}`);
  };

  const renderLibraryTree = (libs: LibraryWithSlices[], depth = 0): React.ReactNode => {
    return (
      <div className={depth > 0 ? "ml-4 border-l border-gray-200 dark:border-gray-700 pl-4" : ""}>
        {libs.map((library) => {
          const unmappedCount = library.slices?.filter(s => !s.has_product_mapping).length || library.unmapped_slices_count || 0;

          return (
            <div key={library.id} className="py-2">
              {/* Library header */}
              <div
                className="flex items-center gap-2 p-2 bg-white dark:bg-slate-800 rounded border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer"
                onClick={() => {
                  if (library.children_count > 0 || library.total_images_count > 0) {
                    toggleLibrary(library.id);
                  }
                }}
              >
                {library.children_count > 0 || library.total_images_count > 0 ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleLibrary(library.id);
                    }}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-slate-600 rounded"
                  >
                    {expandedLibraries.has(library.id) ? (
                      <ChevronDown size={18} />
                    ) : (
                      <ChevronRight size={18} />
                    )}
                  </button>
                ) : (
                  <div className="w-6" />
                )}

                <ImageIcon size={18} className="text-blue-500" />
                <span className="font-medium flex-1">{library.name}</span>
                <div className="flex gap-3 text-xs text-muted-foreground">
                  <span title="Összesen slice">
                    {library.total_slices_count} slice
                  </span>
                  {unmappedCount > 0 && (
                    <span title="Nem leképezett slice-ok" className="text-orange-600 dark:text-orange-400 font-medium">
                      {unmappedCount} azonosítatlan
                    </span>
                  )}
                </div>
              </div>

              {/* Slices list */}
              {expandedLibraries.has(library.id) && (
                <div className="mt-2 space-y-2">
                  {library.slicesLoading ? (
                    <div className="p-4 text-center text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                      Slice-ok betöltése...
                    </div>
                  ) : library.slices && library.slices.length > 0 ? (
                    <>
                      <Button
                        onClick={() => handleGroupAnnotation(library)}
                        className="w-full"
                        variant="default"
                      >
                        <Tag className="mr-2 h-4 w-4" />
                        Csoportos Megjelölés ({library.slices.length} slice{unmappedCount > 0 ? `, ${unmappedCount} azonosítatlan` : ''})
                      </Button>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {library.slices.map((slice) => (
                          <div key={slice.id} className="relative group">
                            {slice.slice_url ? (
                              <>
                                <img
                                  src={slice.slice_url}
                                  alt={`Slice ${slice.slice_number}`}
                                  className={`w-full h-32 object-cover rounded border ${!slice.has_product_mapping ? 'border-orange-400 dark:border-orange-500' : 'border-gray-300 dark:border-slate-600'}`}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 rounded transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                  <a
                                    href={slice.slice_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 bg-blue-500 hover:bg-blue-600 rounded-full text-white"
                                    title="Slice megnyitása"
                                  >
                                    <Download size={16} />
                                  </a>
                                  <button
                                    onClick={() => handleDeleteSlice(slice.id)}
                                    className="p-2 bg-red-500 hover:bg-red-600 rounded-full text-white"
                                    title="Slice törlése"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </>
                            ) : (
                              <div className="w-full h-32 bg-gray-200 dark:bg-slate-700 rounded flex items-center justify-center text-muted-foreground">
                                <span className="text-xs">Slice {slice.slice_number}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground italic p-2">Nincsenek slice-ok ebben a könyvtárban.</p>
                  )}
                </div>
              )}

              {/* Recursive children */}
              {expandedLibraries.has(library.id) && library.children && library.children.length > 0 && (
                renderLibraryTree(library.children, depth + 1)
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <React.Fragment>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Slice-ok a Könyvtárakban</CardTitle>
              <CardDescription>
                Válassza ki a könyvtárat a slice-ok megtekintéséhez
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchLibraries}
              disabled={loading}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Frissítés
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-muted-foreground">Betöltés...</p>
          ) : libraries.length === 0 ? (
            <p className="text-center text-muted-foreground">Nincsenek könyvtárak.</p>
          ) : (
            renderLibraryTree(libraries)
          )}
        </CardContent>
      </Card>
    </React.Fragment>
  );
});

SliceList.displayName = "SliceList";

export default SliceList;
